// Results by the target's wound states (rules/woundStates.js): a clean bar
// with the out-of-action glyphs under it, and one row per state with what
// share of the rolls ends exactly there and how many end there or worse.
//
// The bar and the list point at each other (useStatesUi): hovering a segment
// lights its row; hovering a row lights every segment that counts towards it
// (that is what "at least" means); tapping a side's heading fades the other
// side; Unconscious and Dead, the states that take a trooper out of the fight,
// also give the chance over three orders. Phones tap where desktops hover.
import {useState, useSyncExternalStore} from 'react';
import {StateGlyph} from './icons.jsx';
import {pct, shows} from '../results/summary.js';
import {useT} from './i18n.jsx';

/** @typedef {'active' | 'reactive'} Role */
/** @typedef {import('../types.js').Summary} Summary */
/** @typedef {import('../types.js').WoundState} WoundState */
/** @typedef {import('../types.js').StateBarSegment} StateBarSegment */
/** What the bar and the list point at: a hovered segment or row, a focused side, a row tapped open.
 * @typedef {{hover: {kind: 'seg' | 'row', side: Role, index: number} | null, focus: Role | null, open: string | null}} StatesState */
/** The small store the bar and the list share (createStatesUi).
 * @typedef {{get: () => StatesState, subscribe: (f: () => void) => () => void, setHover: (hover: StatesState['hover']) => void,
 *   setFocus: (focus: Role | null) => void, setOpen: (open: string | null) => void}} StatesUi */

const ORDERS = 3;
/** @param {number} p */
const overOrders = (p) => 1 - (1 - p) ** ORDERS;

// The bar and the list share this small store instead of React state in the
// sheet: hovering re-renders only the bar and the list that read it, not the
// whole results sheet and its ledger. The object itself never changes.
//   hover  {kind: 'seg' | 'row', side, index}
//   focus  'active' | 'reactive'
//   open   `${side}:${key}`, tapped open
/** @returns {StatesUi} */
function createStatesUi() {
  /** @type {StatesState} */
  let state = {hover: null, focus: null, open: null};
  /** @type {Set<() => void>} */
  const subs = new Set();
  /** @param {Partial<StatesState>} patch */
  const set = (patch) => {
    state = {...state, ...patch};
    subs.forEach((f) => f());
  };
  return {
    get: () => state,
    subscribe: (f) => { subs.add(f); return () => subs.delete(f); },
    setHover: (hover) => set({hover}),
    setFocus: (focus) => set({focus}),
    setOpen: (open) => set({open}),
  };
}

export function useStatesUi() {
  const [ui] = useState(createStatesUi);
  return ui;
}

/** @type {StatesState} */
const NO_UI = {hover: null, focus: null, open: null};
const noSubscribe = () => () => {};
/**
 * The current hover / focus / open of `ui` (null: none, nothing changes).
 * @param {StatesUi | null | undefined} ui
 */
const useUiState = (ui) => useSyncExternalStore(ui ? ui.subscribe : noSubscribe, ui ? ui.get : () => NO_UI);

/**
 * @param {StatesState} cur
 * @param {StateBarSegment} seg
 */
const dimSeg = (cur, seg) => {
  if (cur.focus && seg.side !== cur.focus) return true;
  const h = cur.hover;
  if (h?.kind !== 'row') return false;
  return seg.side !== h.side || seg.index < h.index;
};
/**
 * @param {StatesState} cur
 * @param {Role} side
 * @param {number} index
 */
const dimRow = (cur, side, index) => {
  if (cur.focus && side !== cur.focus) return true;
  const h = cur.hover;
  if (h?.kind === 'seg') return h.side !== side || h.index !== index;
  return false;
};

// glyphs: false (bar only), true (bar, glyphs under it) or 'only' (just the
// glyph row: the phone sheet draws the bar in its top and the glyphs below).
// ui null: a plain bar (the closed phone card, where a tap opens the sheet).
/** A summary with the target's states (summarize with targets).
 * @typedef {Summary & {states: NonNullable<Summary['states']>, stateBar: NonNullable<Summary['stateBar']>}} StatesSummary */

/** @param {{s: StatesSummary, ui?: StatesUi | null, glyphs?: boolean | 'only', className?: string}} props */
export function StateBar({s, ui, glyphs = false, className = ''}) {
  const t = useT();
  const cur = useUiState(ui);
  const segs = s.stateBar.filter((b) => shows(b.chance));
  let x = 0;
  /** @type {{b: StateBarSegment, glyph: string, left: number}[]} */
  const marks = [];
  for (const b of segs) {
    const st = b.side === 'none' ? null : s.states[b.side][b.index];
    if (st?.glyph) marks.push({b, glyph: st.glyph, left: x + 50 * b.chance});
    x += 100 * b.chance;
  }
  /** @param {StateBarSegment} b */
  const tip = (b) => {
    if (b.side === 'none') return `${t('results.nobodyWounded')} · ${pct(b.chance)}`;
    const st = s.states[b.side][b.index];
    return t('results.stateTip', {state: st.label, exactly: pct(st.exactly), atLeast: pct(st.atLeast)});
  };
  /** @param {StateBarSegment} b */
  const hov = (b) => (ui && b.side !== 'none' ? {
    // Not 'none': a side's segment.
    onMouseEnter: () => ui.setHover({kind: 'seg', side: /** @type {Role} */ (b.side), index: b.index}),
    onMouseLeave: () => ui.setHover(null),
    // A tap also reaches the bar's own button (ResultBars), which shows the wound list.
    onClick: () => ui.setHover({kind: 'seg', side: /** @type {Role} */ (b.side), index: b.index}),
  } : {});
  return (
    <>
      {glyphs !== 'only' && <div className={`bar sbar ${className}`} role="img" aria-label={t('results.woundsByState')}>
        {segs.map((b) => (
          <span key={`${b.side}${b.key}`} className={`sseg${b.side === 'none' ? ' seg-none' : ''}${dimSeg(cur, b) ? ' dim' : ''}`}
            style={{width: `${100 * b.chance}%`, background: b.shade ?? undefined}} title={tip(b)} {...hov(b)} />
        ))}
      </div>}
      {glyphs && (
        <div className="sglyphs" aria-hidden="true">
          {marks.map(({b, glyph, left}) => (
            <span key={`${b.side}${b.key}`} className={`c-${b.side}${dimSeg(cur, b) ? ' dim' : ''}`} style={{left: `${left}%`}}><StateGlyph glyph={glyph} size={14} /></span>
          ))}
        </div>
      )}
    </>
  );
}

/** @param {{st: WoundState, side: Role, ui: StatesUi, cur: StatesState}} props */
function StateRow({st, side, ui, cur}) {
  const t = useT();
  const id = `${side}:${st.key}`;
  const segHover = cur.hover?.kind === 'seg' && cur.hover.side === side && cur.hover.index === st.index;
  const rowHover = cur.hover?.kind === 'row' && cur.hover.side === side && cur.hover.index === st.index;
  const more = st.outOfFight && (cur.open === id || segHover || rowHover);
  return (
    <div className={`srow${dimRow(cur, side, st.index) ? ' dim' : ''}${rowHover || segHover ? ' on' : ''}`} tabIndex={0}
      role="button" aria-expanded={st.outOfFight ? more : undefined}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ui.setOpen(cur.open === id ? null : id); } }}
      onMouseEnter={() => ui.setHover({kind: 'row', side, index: st.index})} onMouseLeave={() => ui.setHover(null)}
      onFocus={() => ui.setHover({kind: 'row', side, index: st.index})} onBlur={() => ui.setHover(null)}
      onClick={() => ui.setOpen(cur.open === id ? null : id)}>
      <span className={`c-${side}`}><StateGlyph glyph={st.glyph} /></span>
      {/* Rendered by then (state/matchupView.js renderMatchup). */}
      <span className={`k${st.key === 'dead' ? ' dead' : ''}`}>{/** @type {string} */ (st.label)}{st.shock && <span className="stag">{t('ammo.SHOCK')}</span>}</span>
      <span className="track"><span className="b" style={{width: `max(2px, ${100 * st.atLeast}%)`, background: st.shade}} /></span>
      <span className="ex">{pct(st.exactly)}</span>
      <span className="p">{pct(st.atLeast)}</span>
      {more && <span className="more">{t('results.overOrders', {now: pct(st.atLeast), over: pct(overOrders(st.atLeast)), orders: ORDERS})}</span>}
    </div>
  );
}

// A row that hurts nobody: grey, and nothing in the bar to point at.
/** @param {{glyph: string, label: string, p: number, dim?: boolean}} props */
function PlainRow({glyph, label, p, dim}) {
  if (!shows(p)) return null;
  return (
    <div className={`srow plain${dim ? ' dim' : ''}`}>
      <span><StateGlyph glyph={glyph} /></span>
      <span className="k">{label}</span>
      <span className="track"><span className="b seg-none" style={{width: `max(2px, ${100 * p}%)`}} /></span>
      <span className="ex">{pct(p)}</span>
      <span className="p">{pct(p)}</span>
    </div>
  );
}

/** @param {{s: StatesSummary, ui: StatesUi}} props */
export function StateList({s, ui}) {
  const t = useT();
  const cur = useUiState(ui);
  /**
   * @param {Role} side
   * @param {string} text
   */
  const head = (side, text) => (
    <button type="button" className={`shead${cur.focus && cur.focus !== side ? ' dim' : ''}`} aria-pressed={cur.focus === side}
      onClick={() => ui.setFocus(cur.focus === side ? null : side)}>
      <span className={`t c-${side}`}>{text}</span><span className="lab">{t('results.exactly')}</span><span className="lab">{t('results.atLeastHead')}</span>
    </button>
  );
  /** @param {Role} side */
  const rows = (side) => s.states[side].filter((st) => shows(st.atLeast)).map((st) => <StateRow key={st.key} st={st} side={side} ui={ui} cur={cur} />);
  // Won, every hit saved: under the side that won, grey like nobody wounded
  // in the bar (extra information, not always important).
  /** @param {Role} side */
  const saves = (side) => <PlainRow glyph="save" label={t('results.savesAll')} p={s.saved[side]} dim={Boolean(cur.focus && cur.focus !== side)} />;
  return (
    <div className="slist">
      {shows(s.win.active) && head('active', `${t('results.sideWins', {side: t('side.active')})} · ${pct(s.win.active)}`)}
      {shows(s.win.active) && rows('active')}
      {shows(s.win.active) && saves('active')}
      {shows(s.win.none) && <div className={`shead static${cur.focus ? ' dim' : ''}`}><span className="t">{t('results.nobodyWins')} · {pct(s.win.none)}</span></div>}
      {shows(s.win.none) && <PlainRow glyph="tie" label={t('results.bothMissOrTie')} p={s.win.none} dim={Boolean(cur.focus)} />}
      {shows(s.win.reactive) && head('reactive', `${t('results.sideWins', {side: t('side.reactive')})} · ${pct(s.win.reactive)}`)}
      {shows(s.win.reactive) && rows('reactive')}
      {shows(s.win.reactive) && saves('reactive')}
    </div>
  );
}
